import React from "react";
import { motion } from "framer-motion";

const rows = [
  { label: "Tuition (annual)", france: "$3,400 – $4,600", us: "$12,000 – $45,000" },
  { label: "Room & board", france: "$7,000 – $12,000", us: "$13,900 – $15,900" },
  { label: "Health coverage", france: "Bundled, Sécurité sociale", us: "$2,000 – $3,000 extra" },
  { label: "Housing subsidy (CAF)", france: "$100 – $270 / month back", us: "None" },
  { label: "Total year 1 (all-in)", france: "$14,000 – $21,000", us: "$30,000 – $65,000+" },
  { label: "Typical bachelor's length", france: "3 years", us: "4 years" },
];

const fade = (i = 0) => ({
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.05 } },
});

export default function CostComparison() {
  return (
    <section className="bg-[#f5f1e8] py-24 md:py-32" data-testid="cost-comparison">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
          className="grid md:grid-cols-12 gap-10 items-end"
        >
          <motion.div variants={fade(0)} className="md:col-span-6">
            <div className="flex items-center gap-3 mb-6">
              <span className="rule-gold" /><span className="eyebrow">09 · The math</span>
            </div>
            <h2 className="serif text-5xl md:text-7xl leading-[0.95] tracking-tight text-[#0d1b3e]">
              Earn a world-class degree, <br />
              <em className="text-[#b8892b] not-italic serif italic">for far less than most U.S. universities.</em>
            </h2>
          </motion.div>
          <motion.p variants={fade(1)} className="md:col-span-6 text-lg text-[#4a5570] leading-relaxed max-w-xl md:ml-auto">
            Even after France standardised non-EU tuition for 2026–27, a full year at a French public university still
            costs less than a single semester at most US out-of-state or private schools.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }}
          className="mt-16 border-y border-[#0d1b3e]/15"
        >
          <div className="grid grid-cols-12 border-b border-[#0d1b3e]/15 py-4 text-xs uppercase tracking-[0.22em] text-[#4a5570]">
            <div className="col-span-12 md:col-span-4 mb-2 md:mb-0">Category</div>
            <div className="col-span-6 md:col-span-4 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#b8892b] inline-block" /> France
            </div>
            <div className="col-span-6 md:col-span-4">United States</div>
          </div>
          {rows.map((r, i) => (
            <motion.div key={r.label} variants={fade(i + 2)}
              className={`grid grid-cols-12 py-5 md:py-8 border-b border-[#0d1b3e]/10 ${i === rows.length - 2 ? "bg-[#0d1b3e]/5" : ""}`}>
              <div className="col-span-12 md:col-span-4 serif text-lg md:text-3xl text-[#0d1b3e]">{r.label}</div>
              <div className="col-span-6 md:col-span-4 text-[#0d1b3e] font-medium text-sm md:text-xl mt-2 md:mt-0">
                <span className="text-[#b8892b] mr-1">·</span>{r.france}
              </div>
              <div className="col-span-6 md:col-span-4 text-[#4a5570] text-sm md:text-xl mt-2 md:mt-0 line-through decoration-[#4a5570]/30">
                {r.us}
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mt-10 grid md:grid-cols-2 gap-8"
        >
          <div className="serif text-3xl text-[#0d1b3e] leading-tight max-w-lg">
            One French year saves roughly <em className="italic text-[#b8892b]">$40,000</em> versus a comparable US private school year.
          </div>
          <p className="text-xs text-[#4a5570] leading-relaxed max-w-md md:ml-auto">
            Figures are estimates based on published 2026–27 tuition rates and national averages; actual costs vary by
            university, program, and city. France's non-EU tuition structure changed in 2026, always confirm current
            rates directly with the university or Campus France before finalising a budget.
          </p>
        </motion.div>

        <div className="mt-10 flex justify-center">
          <a href="/calculator" className="btn-sharp btn-sharp-gold" data-testid="cost-calc-cta">
            Calculate my estimated costs
          </a>
        </div>
      </div>
    </section>
  );
}
